// API client for the canonical conversation read endpoints. Attaches the Entra
// access token as a bearer (same-origin; the UI is served by the Fastify app).

import { getAccessToken } from './msal.js'
import { PARK_ALERT_MINUTES } from './parking.js'
import { prefetchOrderSummary, takePrefetched } from './orderSummaryPrefetch.js'

// headers: extra request headers (the order-summary reads' X-Order-Fresh /
// X-Items-Part, 2026-10-29).
async function getJson(path, { headers = null } = {}) {
  const token = await getAccessToken()
  const res = await fetch(path, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers ?? {}),
    },
  })
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  return res.json()
}

async function postJson(path, body, { signal } = {}) {
  const token = await getAccessToken()
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    // Optional: lets a caller cancel a long request (the assistant's Stop
    // button). Aborting closes the socket, which the server watches so it stops
    // working rather than just losing its audience.
    signal,
  })
  if (!res.ok) {
    // Attach the status + parsed body so callers can react to specific
    // failures (e.g. 409 already_decided carries the current suggestion).
    const err = new Error(`${path} -> ${res.status}`)
    err.status = res.status
    err.body = await res.json().catch(() => null)
    throw err
  }
  return res.json()
}

export function fetchMe() {
  return getJson('/api/me')
}

// Fetch conversations with optional filters + pagination. `from`/`to` are ISO
// datetime strings (last-message range). `updatedSince` is a SEPARATE ISO
// cutoff for App.jsx's 10s incremental poll — it filters on when the row was
// actually inserted/last touched in Postgres (conversations.updated_at), not
// on the source message's own timestamp `from`/`to` use, so it never misses a
// conversation whose last_message_at is older than the poll's own tight
// window (see repositories/conversations.js's own comment on updatedSince).
// Returns { conversations, total } where total is the count matching the
// filters (ignoring limit/offset) for paging. `company` is the queue's
// Company selection — brand codes ('1A', 'AM'), sent as a server-side filter
// (not just a client-side re-filter of an already-fetched page) because a
// company that isn't surfaced yet is withheld by the server entirely unless
// the request names it. See the route's resolveAccountFilter().
export function fetchConversations({ limit, offset, from, to, updatedSince, status, intent, q, since, company, toggles, reviewedByMgmt, spamSetBy, actedByUser, automated, includeToAddress, sortBy } = {}) {
  const params = new URLSearchParams()
  if (limit != null) params.set('limit', String(limit))
  if (offset != null) params.set('offset', String(offset))
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (updatedSince) params.set('updatedSince', updatedSince)
  if (status) params.set('status', status)
  // intent/actedByUser are multi-select on the client -- an empty array is a
  // real, valid "nothing checked" state (meaning unfiltered), so join THEN
  // check truthiness of the resulting string, not the array itself (an empty
  // array is truthy in JS and would otherwise send an empty `intent=` param).
  const intentParam = Array.isArray(intent) ? intent.join(',') : intent
  if (intentParam) params.set('intent', intentParam)
  if (q) params.set('q', q)
  if (since) params.set('since', String(since))
  if (company) params.set('company', Array.isArray(company) ? company.join(',') : company)
  // The Queue's 1A / AM / Support toggles (2026-10-07): sent even when empty
  // (nothing selected means nothing shown), like includeToAddress below.
  if (toggles !== undefined) params.set('toggles', Array.isArray(toggles) ? toggles.join(',') : toggles)
  if (reviewedByMgmt !== undefined) params.set('reviewedByMgmt', String(reviewedByMgmt))
  if (spamSetBy) params.set('spamSetBy', Array.isArray(spamSetBy) ? spamSetBy.join(',') : spamSetBy)
  const actedByUserParam = Array.isArray(actedByUser) ? actedByUser.join(',') : actedByUser
  if (actedByUserParam) params.set('actedByUser', actedByUserParam)
  // Only ever sent by the caller when the Channel filter includes
  // m365_email (App.jsx) — same "meaningless outside its own scope, so the
  // UI simply never sends it" posture as spamSetBy/reviewedByMgmt above.
  // INCLUDE-style (which addresses to SHOW) — but unlike intent/actedByUser's
  // own include-style "nothing checked means unfiltered" convention, an
  // EMPTY array here is a real, deliberate "show none" request (the CSR
  // unticked every address), not "no opinion" — so this checks `undefined`,
  // not truthiness, and sends the param (as an empty string) even when the
  // array is empty. App.jsx only ever passes `undefined` when the filter is
  // genuinely out of scope (Channel doesn't include m365_email) or every
  // address is ticked (equivalent to unfiltered) — see its own
  // includeToAddressFilter comment for the full story.
  if (includeToAddress !== undefined) {
    params.set('includeToAddress', Array.isArray(includeToAddress) ? includeToAddress.join(',') : includeToAddress)
  }
  if (automated !== undefined) params.set('automated', String(automated))
  if (sortBy) params.set('sortBy', sortBy)
  const qs = params.toString()
  return getJson(`/api/conversations${qs ? `?${qs}` : ''}`).then((d) => ({
    conversations: d.conversations ?? [],
    total: d.total ?? (d.conversations?.length ?? 0),
    // queueTotal = non-closed (open+pending+escalated); statusCounts = full split.
    queueTotal: d.queueTotal ?? null,
    statusCounts: d.statusCounts ?? null,
  }))
}

// Distinct real users who have ever acted on a conversation (decided a
// suggestion, manually closed one, or processed a proposed step) — populates
// the "filter by acting user" dropdown. Returns [] on any error so the
// filter just degrades to empty rather than breaking the conversation list.
export function fetchConversationActors() {
  return getJson('/api/conversations/actors').then((d) => d.actors ?? []).catch(() => [])
}

// Registry-sourced options for the "To Address" filter (only shown when the
// Channel filter includes m365_email) — channels/m365/config.js's mailbox
// registry, not something the client invents. Returns [] on any error, same
// degrade-quietly posture as fetchConversationActors() above.
//
// Returns the directory: { address, company, testMailbox } per address, the
// owning company from the mailbox registry (channels/m365/config.js's
// m365AddressDirectory()). The To Address checklist renders the addresses; the
// auto-select groups them by company (toAddressAutoSelect.js).
export function fetchM365Addresses() {
  return getJson('/api/conversations/m365-addresses').then((d) => d.directory ?? []).catch(() => [])
}

// Every address CXHub owns (mailboxes, aliases, send identities), for the
// reply box's Reply-All CC default to leave out (2026-10-08). Fetched once per
// page load; an empty list on failure (the server refuses an own address as a
// CC anyway).
let ownAddressesPromise = null
export function fetchM365OwnAddresses() {
  if (!ownAddressesPromise) {
    ownAddressesPromise = getJson('/api/conversations/m365-addresses')
      .then((d) => d.ownAddresses ?? [])
      .catch(() => { ownAddressesPromise = null; return [] })
  }
  return ownAddressesPromise
}

// Manually pull the latest N eBay buyer conversations into CXHub (read-only on
// eBay's side). Returns { conversationsScanned, messagesIngested }. On failure
// throws an Error whose message is the server's detail (e.g. "eBay reads are
// disabled…") so the UI can show why.
export async function ingestEbay(limit = 10, conversationType = null) {
  const token = await getAccessToken()
  const payload = { limit }
  if (conversationType) payload.conversationType = conversationType
  const res = await fetch('/api/ebay/ingest', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(body.detail || body.error || `ingest -> ${res.status}`)
    err.code = body.error || null
    throw err
  }
  return body
}

// Live eBay read/write flags + environment, for the admin API reference page.
// Returns { read, write, env, marketplace }, or null on any error so the page
// can fall back to static defaults rather than break.
export function fetchEbayFlags() {
  return getJson('/api/ebay/flags').catch(() => null)
}

// Today's-schedule header widget. Returns the raw Schedule Hub response
// shape ({ day_off, shift_start, shift_end, breaks, lunch, notes, ... }) for
// the logged-in caller's own schedule, or null on ANY non-200 (not pilot-
// listed, integration disabled, not a real Schedule Hub employee, Schedule
// Hub itself erroring) -- the widget renders nothing either way, so there's
// no need for callers to distinguish the reason.
export function fetchTodaySchedule() {
  return getJson('/api/schedule/today').catch(() => null)
}

// "My Stats" self-service page -- the caller's own AHT + contact counts for
// the last 12 weeks. Unlike fetchTodaySchedule, errors are NOT swallowed
// here: this is a whole page's content, not a header widget, so the caller
// needs to show a real error state rather than silently rendering nothing.
export function fetchMyStats() {
  return getJson('/api/my-stats/weekly')
}

// Same page's second table -- last 3 complete calendar months plus the
// current, in-progress one. Same response shape as fetchMyStats (month
// labels ride the same `weekLabels` field name), same not-swallowed error
// convention.
export function fetchMyStatsMonthly() {
  return getJson('/api/my-stats/monthly')
}

// Admin API Status: read/write state of every external integration, plus the
// health (days-to-expiry) of long-lived credentials. Returns
// { integrations, tokenHealth }, both empty on error.
export function fetchIntegrations() {
  return getJson('/api/admin/integrations')
    .then((d) => ({
      integrations: d.integrations ?? [],
      tokenHealth: d.tokenHealth ?? [],
      ebayConsentCompanies: d.ebayConsentCompanies ?? [],
    }))
    .catch(() => ({ integrations: [], tokenHealth: [], ebayConsentCompanies: [] }))
}

// Admin: the eBay seller-consent URL for one account. The server only issues the
// consent state to a signed-in admin, so this is fetched with the bearer token and
// the URL is then opened in a browser signed into that eBay seller account.
export function startEbayConsent(company) {
  return getJson(`/api/ebay/oauth/authorize?company=${encodeURIComponent(company)}`)
}

// Admin: per-mailbox M365 ingest health — whether mail arrived and failed to get
// in. A separate call from fetchIntegrations() because it makes Graph calls per
// mailbox, so a slow or unreachable mailbox must not hold up the flag-derived
// integration list beside it.
//
// Reports `failed` rather than swallowing the error into an empty list, unlike
// its neighbour above. On a health surface "everything is fine" and "I could not
// check" must not render the same, or the page reassures you precisely when it
// has stopped working.
export function fetchM365Health() {
  return getJson('/api/admin/m365-health')
    .then((d) => ({ mailboxes: d.mailboxes ?? [], failed: false }))
    .catch(() => ({ mailboxes: [], failed: true }))
}

// Admin → Ops → Health: the graded platform status (services/platform-status.js).
// `fresh` bypasses the server's 30s cache — what the Refresh button sends; the
// 60s auto-refresh takes the cached copy so a tab left open costs nothing.
//
// Same `failed` posture as fetchM365Health() above, for the same reason: this is
// a health surface, and "everything is fine" must not render like "I could not
// check".
export function fetchPlatformStatus({ fresh = false } = {}) {
  return getJson(`/api/admin/status${fresh ? '?fresh=1' : ''}`)
    .then((d) => ({ status: d, failed: false }))
    .catch(() => ({ status: null, failed: true }))
}

// Admin -> Ops -> Alerts: the cards alert triage posted to Teams, kept in the
// app (routes/alerts.js). Throws on error so the page can say so.
export function fetchAlertEvents({ days = 7, rule = null } = {}) {
  const q = new URLSearchParams({ days: String(days) })
  if (rule) q.set('rule', rule)
  return getJson(`/api/alerts?${q}`)
}

// Admin, PII-safe: capture the live eBay response SHAPE (field names + types only,
// no values) to confirm the coverage matrix. Throws on error so the caller can show why.
export function fetchEbayRawShape(limit = 5) {
  return getJson(`/api/ebay/diagnostics/raw-shape?limit=${encodeURIComponent(limit)}`)
}

// Register (or refresh) the current user as actively viewing a conversation.
// Called every ~30 s while the conversation is open; fire-and-forget.
export function pingPresence(conversationId) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/presence`, {}).catch(() => null)
}

// Fetch the list of agents currently viewing a conversation (stale entries
// excluded server-side). Returns [] on any error so the UI degrades gracefully.
export function fetchPresence(conversationId) {
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/presence`)
    .then((d) => d.viewers ?? [])
    .catch(() => [])
}

// Conversation "assignment" (soft-lock) — a separate, additional concept
// layered on top of presence above (which stays unaffected). Claims (or
// refreshes/checks) ownership of a conversation for the current agent.
// Called once on open and every ~30s thereafter, on the same tick as
// pingPresence. Returns null on error so the caller can treat it as "no
// change" rather than crash the heartbeat loop.
// opening: true only on the FIRST heartbeat of an open (2026-10-02,
// parking) — the server unparks the owner's own parked contact on that one
// only, never on a routine 30s tick.
export function heartbeatAssignment(conversationId, { opening = false } = {}) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/assignment/heartbeat`, opening ? { opening: true } : {}).catch(() => null)
}

// Parked contacts (Lab-only behind PARKING_ENABLED; /api/me's parkingEnabled).
// work (2026-10-27, workDraft.js): the CSR's work in progress, { suggestionId,
// payload }, saved with the park in one transaction.
export function parkConversation(conversationId, note, work = null) {
  return postJson(`/api/parks/conversations/${encodeURIComponent(conversationId)}`, { note: note ?? null, ...(work ? { work } : {}) })
}
// The saved work in progress (2026-10-27): read to restore it, autosaved a
// few seconds after the last edit (owner only), and unpark's compare settled.
export function fetchWorkDraft(conversationId) {
  return getJson(`/api/parks/conversations/${encodeURIComponent(conversationId)}/work`).then((d) => d?.work ?? null)
}
export function saveWorkDraft(conversationId, work) {
  return putJson(`/api/parks/conversations/${encodeURIComponent(conversationId)}/work`, work)
}
// The saved work dropped (2026-10-30): "Start from the AI draft instead", or
// saved work that matches the AI draft. Owner only.
export function clearSavedWork(conversationId) {
  return deleteReq(`/api/parks/conversations/${encodeURIComponent(conversationId)}/work`)
}
// Someone else's saved work was restored onto this suggestion (2026-10-30):
// the server records the saver's Roles page id and the time on it.
export function recordRestoredWork(conversationId, suggestionId) {
  return postJson(`/api/parks/conversations/${encodeURIComponent(conversationId)}/work/restored`, { suggestionId })
}
// fromSuggestionId (2026-10-28): the suggestion a Regenerate replaced under
// the CSR's work, for the regenerated compare (not a park).
export function settleParkCompare(conversationId, { suggestionId, resolution, restoreSteps = [], fromSuggestionId = null }) {
  return postJson(`/api/parks/conversations/${encodeURIComponent(conversationId)}/work/settle`, { suggestionId, resolution, restoreSteps, ...(fromSuggestionId ? { fromSuggestionId } : {}) })
}
export function returnParkedToQueue(conversationId) {
  return postJson(`/api/parks/conversations/${encodeURIComponent(conversationId)}/return-to-queue`, {})
}
export function fetchMyParks() {
  return getJson('/api/parks/mine').then((d) => d.parks ?? [])
}
// My open parks AND my pending auto re-queue notices, from the same request
// (2026-10-07): { parks, autoRequeued }.
export function fetchMyParksAndNotices() {
  return getJson('/api/parks/mine').then((d) => ({ parks: d.parks ?? [], autoRequeued: d.autoRequeued ?? [] }))
}

// The one background poll a tab makes (2026-10-09; sessionTick.js drives it):
// assignments, intent priorities, the automation-failure count (admin) and
// parks (mine; every park for an admin) in a single request every 30 s. A
// section the server could not build is null, never missing.
export function fetchSessionTick() {
  return getJson('/api/session/tick')
}
// Ok on an auto re-queue notice.
export function acknowledgeAutoRequeue(parkId) {
  return postJson(`/api/parks/${encodeURIComponent(parkId)}/acknowledge`, {})
}
// Re-Park from an auto re-queue notice (claims it if nobody owns it).
export function reparkConversation(conversationId, parkId) {
  return postJson(`/api/parks/conversations/${encodeURIComponent(conversationId)}/repark`, { parkId })
}
// Admin: every open park, and how many have been parked past the alert threshold.
export function fetchAllParks() {
  return getJson('/api/parks').then((d) => ({ parks: d.parks ?? [], overdueCount: d.overdueCount ?? 0, alertMinutes: d.alertMinutes ?? PARK_ALERT_MINUTES }))
}

// Releases the current agent's assignment (a no-op server-side if they're
// not actually the owner). Fire-and-forget.
export function releaseAssignment(conversationId, pauseId) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/assignment/release`, pauseId ? { pauseId } : {}).catch(() => null)
}

// "Yes, still working on it" — resolves the open inactivity check-in pause.
// Unlike heartbeatAssignment/releaseAssignment, this is a user-initiated
// action from the blocking modal whose result the UI must know about —
// errors propagate, same convention closeConversation uses, not the
// background-call .catch(() => null) pattern.
export function confirmStillWorking(conversationId, pauseId) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/assignment/confirm-still-working`, { pauseId })
}

// Force-claims the conversation for the current agent regardless of who
// currently owns it — what the red "Take Over Contact" button calls.
export function takeoverAssignment(conversationId) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/assignment/takeover`, {}).catch(() => null)
}

// Every currently-active assignment — { [id]: {username, displayName,
// assignedAt} }, ids the caller doesn't care about included and unassigned
// ones simply absent. Deliberately does NOT enumerate the list's ids in the
// query string: at the 500-row page size that URL ran ~20KB and every call
// came back 431 (Request Header Fields Too Large). Returns {} on any error so
// the list just renders with no gray-out rather than breaking.
export function fetchAssignments() {
  return getJson('/api/conversations/assignments')
    .then((d) => d.assignments ?? {})
    .catch(() => ({}))
}

// Shared reconnect/backoff driver behind both SSE subscriptions below (asset
// stream, conversation status stream). Plain fetch() + a manually-parsed
// text/event-stream body, not native EventSource: EventSource can't send an
// Authorization header, and this app authenticates every request with an
// Entra bearer token — putting it in the URL as a query param would leak it
// into server/proxy access logs.
//
// onEvent is called with the parsed JSON of each `data: ` line (the `: ping`
// keep-alive comment is silently skipped). Returns a cleanup function;
// calling it aborts any in-flight connection and stops the reconnect loop for
// good. A dropped connection (network blip, server restart) reconnects on its
// own with a short backoff (starts at 1s, caps at 15s) rather than going
// silent — the access token is re-fetched on every attempt in case it
// rotated between connections.
function subscribeEventStream(url, onEvent) {
  let stopped = false
  let controller = null

  async function connectLoop() {
    let backoffMs = 1000
    while (!stopped) {
      controller = new AbortController()
      try {
        const token = await getAccessToken()
        const res = await fetch(url, {
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        })
        if (!res.ok || !res.body) throw new Error(`${url} -> ${res.status}`)
        backoffMs = 1000 // a successful connection resets the backoff

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (!stopped) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const events = buffer.split('\n\n')
          buffer = events.pop() ?? '' // last chunk may be incomplete — keep it for the next read
          for (const event of events) {
            const dataLine = event.split('\n').find((line) => line.startsWith('data: '))
            if (!dataLine) continue // e.g. the ": ping" keep-alive comment
            try {
              onEvent(JSON.parse(dataLine.slice('data: '.length)))
            } catch {
              // malformed event — skip it, the next one still arrives
            }
          }
        }
      } catch {
        // Network error, abort (cleanup was called), or a non-2xx response —
        // either way fall through to the backoff/reconnect below unless
        // cleanup() already set stopped.
      }
      if (stopped) return
      await new Promise((resolve) => setTimeout(resolve, backoffMs))
      backoffMs = Math.min(backoffMs * 2, 15000)
    }
  }

  connectLoop()

  return () => {
    stopped = true
    controller?.abort()
  }
}

// Push channel for assignment ownership changes — every OTHER logged-in
// agent's conversation list learns about a claim/release/takeover/stale-prune
// immediately instead of waiting for fetchAssignments()'s own poll (still
// kept, just as a slower reconciliation fallback — see App.jsx).
//
// onEvent is called with { conversationId, owner } for each event (owner is
// the ownerView shape, or null for a release/prune).
export function subscribeAssignmentStream(onEvent) {
  return subscribeEventStream('/api/conversations/assignments/stream', onEvent)
}

// Push channel for conversation status changes (close/escalate/reopen) — the
// server-side counterpart to routes/conversations.js's GET /status-stream
// (repositories/conversations.js's broadcastStatusChange(), the only two call
// sites of which are setConversationStatus()/closeConversationManually()).
// Lets a closed-elsewhere conversation drop out of every OTHER agent's Queue
// tab immediately instead of waiting on the 10s incremental poll — see
// App.jsx's tick()/mergeAndSort() for the poll-side half of this fix.
//
// onEvent is called with { conversationId, status } for each event. Same
// in-memory/single-replica caveat as the assignment stream: a second
// Container Apps replica only reaches the subscribers connected to IT.
export function subscribeConversationStatusStream(onEvent) {
  return subscribeEventStream('/api/conversations/status-stream', onEvent)
}

export function fetchConversation(id) {
  return getJson(`/api/conversations/${encodeURIComponent(id)}`)
}

// Deep-linkable /contact/<convNumber> URL boot resolution — the ONLY place
// conv_number is ever looked up; every other read/write in the app is
// UUID-keyed, unchanged. Returns the UUID id, or null on any failure (not
// found, no access, network error) so the caller falls back to the normal
// default view rather than hard-failing app boot.
export function fetchConversationIdByNumber(convNumber) {
  return getJson(`/api/conversations/by-number/${encodeURIComponent(convNumber)}`).then((d) => d.id).catch(() => null)
}

// Downloads one m365_email attachment and saves it via the browser — same
// Blob/createObjectURL/<a download> pattern exportChartPng.js established
// (this codebase's only other file-download path), except the bytes come
// from an authenticated fetch instead of a canvas. `name` is the generic
// label the read API already returned for this attachment (conversations.js's
// attachmentDownloadName) — filenames are deliberately never stored server-
// side, so this is the same name the server's own Content-Disposition sends,
// just reused here to avoid a second round-trip's worth of latency.
export async function downloadAttachment(conversationId, attachmentId, name) {
  const url = await fetchAttachmentObjectUrl(conversationId, attachmentId)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// One attachment's bytes as an object URL: for a download above, and for
// showing an inline email image in place (inlineImages.js). The caller revokes
// the URL when it is done with it.
export async function fetchAttachmentObjectUrl(conversationId, attachmentId) {
  const token = await getAccessToken()
  const path = `/api/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`
  const res = await fetch(path, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  return URL.createObjectURL(await res.blob())
}

// Ops → Prompt Improvement (admin). Non-PII row counts for the current
// filters, so the button can say how many rows the download will hold.
function promptReviewQuery(params) {
  return new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
}

export function fetchPromptReviewCounts(params = {}) {
  return getJson(`/api/admin/prompt-review/counts?${promptReviewQuery(params)}`)
}

// Downloads the PII-redacted JSONL export — same Blob/<a download> pattern as
// downloadAttachment() above. The filename comes from the server's
// Content-Disposition so the two never disagree.
export async function downloadPromptReviewExport(params = {}) {
  const token = await getAccessToken()
  const path = `/api/admin/prompt-review/export?${promptReviewQuery(params)}`
  const res = await fetch(path, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'prompt-review.jsonl'
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return name
}

// Reporting -> Weekly Report tab. Same fetch-with-token -> Blob -> hidden
// <a download> pattern as downloadPromptReviewExport() above, just for a
// CSV instead of JSONL -- no query params, both exports compute their own
// fixed window server-side (trailing 6 weeks / most recent completed week).
async function downloadCsv(path, fallbackName) {
  const token = await getAccessToken()
  const res = await fetch(path, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return name
}

export function downloadWeeklyExport() {
  return downloadCsv('/api/admin/reporting/weekly-export.csv', 'cxhub_weekly_export.csv')
}

export function downloadWeeklyAgentBreakdown() {
  return downloadCsv('/api/admin/reporting/weekly-export-agents.csv', 'cxhub_agent_breakdown.csv')
}

// Unlike the two above, this one calls out to Schedule Hub over the network
// server-side and can genuinely fail upstream (503 disabled, 502 upstream
// error) -- downloadCsv()'s shared `!res.ok` check already throws in either
// case, so the caller's existing try/catch handles it the same way. One
// combined file since 2026-09-24 (Company Total row + one row per team,
// with Occupancy/Utilization columns) -- replaces what used to be two
// separate downloads.
export function downloadWeeklyLaborUtilization() {
  return downloadCsv('/api/admin/reporting/weekly-export-labor.csv', 'cxhub_labor_utilization.csv')
}

// AI suggestions for a conversation (newest first).
export function fetchSuggestions(conversationId) {
  return getJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/suggestions`).then((d) => d.suggestions ?? [])
}

// The eBay inquiry/case History, re-read from eBay on open (2026-10-23): the
// server reads the inquiry (cached a few minutes), merges new entries into the
// suggestion's stored History and returns { history, refreshed }. history is
// the stored History when nothing could be read.
export function refreshCaseHistory(suggestionId) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/case-history/refresh`, {})
}

// Shadow drafts for a conversation (newest first). Admin-only; a CSR gets 403.
export function fetchShadowDrafts(conversationId) {
  return getJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/shadow-drafts`).then((d) => d.shadowDrafts ?? [])
}

// Saves an admin's edit of one step preview in the agent column (2026-10-23):
// values { reasonCode, items, shippingMethod, address, note } for the step's
// own fields. Stored on the shadow draft only; nothing is processed or sent.
// Returns { shadowDraft } with its previews.
export function saveShadowStepEdit(conversationId, shadowId, key, values) {
  return putJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/shadow-drafts/${encodeURIComponent(shadowId)}/steps/${encodeURIComponent(key)}`, { values })
}

// Invoice-copy attachments (invoice_request, m365_email Website accounts
// only) — rendered on demand server-side, never stored. { attachments: [{
// name, invoiceId, dataUrl }], skipped: [erpOrderId, ...], reason } — reason
// (2026-10-29) why nothing could be attached, worded by @cxhub/shared's
// invoiceUnavailableText(). A contact out of the invoice scope (400
// invoice_attachments_out_of_scope) is that reason too, not an error.
export async function fetchInvoiceAttachments(conversationId) {
  const path = `/api/conversations/${encodeURIComponent(conversationId)}/invoice-attachments`
  const token = await getAccessToken()
  const res = await fetch(path, { headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
  if (res.status === 400) {
    const body = await res.json().catch(() => null)
    if (body?.error === 'invoice_attachments_out_of_scope') return { attachments: [], skipped: [], reason: { code: 'out_of_scope' } }
  }
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  return res.json()
}

// Live TRQ cancel-reason catalog (2026-09-28) — { inputOptions: [{ value,
// label }], error: string|null }. Used as a client-side fallback wherever a
// select_reason_code step was persisted before this deploy with no
// inputOptions of its own, and by the manual Cancel Order / Cancel
// Replacement Order panels.
export function fetchCancelReasonCodes(conversationId) {
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/cancel-reason-codes`)
}

// Generate a fresh suggestion for the conversation's latest inbound turn.
// ifChanged (Tier-3): the opportunistic on-open refresh passes it so the server
// reuses the pending suggestion when its draft would be unchanged (order data is
// still re-pulled fresh) instead of paying the full draft+grade cost. The manual
// Regenerate button omits it, forcing a fresh generation.
export function generateSuggestion(conversationId, { ifChanged = false } = {}) {
  const qs = ifChanged ? '?ifChanged=true' : ''
  // A 202 { deferred: 'trq_retry', dueAt, attempt, maxAttempts } (2026-10-14):
  // the turn waits on a TRQ retry and no suggestion was made. Thrown as an
  // error carrying `deferral`, so every caller's catch can tell it apart from
  // "nothing to answer" (404 no_inbound_turn).
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/suggest${qs}`, {}).then((d) => {
    if (d?.deferred) {
      const err = new Error('Order not found in TRQ yet')
      err.status = 202
      err.deferral = { deferred: d.deferred, dueAt: d.dueAt ?? null, attempt: d.attempt ?? null, maxAttempts: d.maxAttempts ?? null }
      throw err
    }
    return d.suggestion
  })
}

// Admin-only: force-regenerate under a manually-chosen intent (sticky —
// every future regeneration for this conversation uses it too, until an
// admin picks a different one or clears it with intent: null). Returns
// { suggestion, conversation }.
export function setIntentOverride(conversationId, intent) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/intent-override`, { intent })
}

// Admin-only: put a conversation on a different team (Customer Service or
// Support) and re-run classification and generation under it. Sticky, like
// the intent override; team: null clears it back to the account's own team.
// Returns { suggestion, conversation }.
export function setTeamOverride(conversationId, team) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/team-override`, { team })
}

// Any csr may call this — corrects which order a contact is tied to when
// the automatic TRQ lookup picked the wrong one out of several candidates.
// Sticky — every future regeneration for this conversation uses it too,
// until cleared with erpOrderId: null or re-set. The server 400s if
// erpOrderId isn't one of the conversation's current candidateOrders.
// Returns { suggestion, conversation }.
export function setErpOrderIdOverride(conversationId, erpOrderId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/erp-order-override`, { erpOrderId })
}

// Any agent may edit or add the contact's VIN from the header (2026-10-21).
// vin: a 17-character VIN (a failed check digit is allowed), or null to
// clear back to the VIN CXHub found. Does not regenerate. Returns
// { conversation, vin, checkDigitOk, vinDecodeStatus, vinDecodeResult, vinOverride }.
export function setVinOverride(conversationId, vin) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/vin-override`, { vin })
}

// Manually close a pending conversation whose reply/resolution happened
// outside the normal decision flow — status-only, no suggestion decision
// involved. Only valid when the conversation is currently 'pending'.
// Returns { conversation }.
export function closeConversation(conversationId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/close`, {})
}

// Reopen a closed conversation so it lands back in the queue — admin
// only, status-only, no suggestion regenerated. Only valid when the
// conversation is currently 'closed'. Returns { conversation }.
export function reopenConversation(conversationId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/reopen`, {})
}

// Admin takeover of a contact queued for Flows automation (migration 0087) —
// races automation's own claim; a 409 (automation_running or not_waiting)
// means the race resolved the other way or the conversation moved on its
// own in the meantime. See SuggestionPanel.jsx's own "Take over" banner/
// confirm-dialog flow, the only caller.
export function takeoverAutomation(conversationId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/automation-takeover`, {})
}

// Bulk: generate suggestions for all conversations without one yet (capped).
// Returns { candidates, generated, failed }.
export function generateAllSuggestions() {
  return postJson('/api/ai/suggestions/generate-all', {})
}

// Record a human decision (approved | edited | escalated | dismissed). Returns
// { suggestion, conversationStatus, executionErrors, replySendError } so the
// caller can update the contact tag and surface any post-decision failures.
// insertedTemplate: { folder, title } of the template the CSR last inserted
// in the reply box, if any (ai_suggestions.sent_template, migration 0092).
// transferNote: status 'transferred' (Transfer to Support, 2026-10-08).
// closeNote (2026-10-13): Close - No Reply Needed on a task contact.
// confirmRecentReply (related contacts, 2026-10-20): the person confirmed
// "We replied to this customer on #… n minutes ago. Send anyway?"; without
// it a recent reply on a related contact is a 409 recent_group_reply.
// closeAnsweredGroup (related contacts, 2026-10-21): { answeredId } on a
// group primary's Close – No Reply Needed from the already-answered banner;
// the group's other open contacts close too, "answered on #<n>".
// acknowledgedWarnings (2026-10-30, docs/decisions/2026-10-30-send-warns-
// not-blocks.md): the codes of the Send warnings the person sent past (Send
// anyway); the server answers 409 send_warnings while one is not.
// confirmPromise (2026-10-23, the older form): the person answered "Send anyway?" for a
// reply that promises an action no step on the contact does (409
// promise_without_step). A promise whose step is not processed (409
// promise_step_unprocessed) is never confirmable.
// skippedSteps: the step indexes the person marked "My reply doesn't need
// this"; recorded on the decision and lifted from the Send check.
// replyStepWarnings (2026-10-30): the Send guard's warnings sent past on the
// agent's own steps, [{ code, index }], recorded with the decision.
export function decideSuggestion(suggestionId, { status, humanReply, actionInputs, escalationNote, transferNote, emailTo, emailSubject, emailAttachments, replyMode, startNewContact, insertedTemplate, ccAddresses, closeNote, confirmRecentReply, closeAnsweredGroup, confirmPromise, skippedSteps, replyStepWarnings, noReply, acknowledgedWarnings } = {}) {
  // noReply (2026-10-27): Approve with no reply sent (an eBay case waiting
  // for the buyer, on the agent draft panel).
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/decision`, { status, humanReply, actionInputs, escalationNote, transferNote, emailTo, emailSubject, emailAttachments, replyMode, startNewContact, insertedTemplate, ccAddresses, closeNote, confirmRecentReply, closeAnsweredGroup, confirmPromise, skippedSteps, acknowledgedWarnings, ...(replyStepWarnings?.length ? { replyStepWarnings } : {}), ...(noReply ? { noReply: true } : {}) })
}

// Retry sending an already-decided suggestion whose outbound send failed
// (replySendError set on decideSuggestion's response) — reuses the existing
// decided row rather than re-deciding it. Returns { suggestion,
// conversationStatus, replySendError }, same shape as decideSuggestion's.
// "Send this reply to the buyer as an eBay message" (2026-11-04): eBay
// refused the reply on the return for its state; the box's text goes to the
// buyer as an eBay message instead (POST .../send-as-ebay-message). Returns
// { suggestion, conversationStatus, replySendError, replyRefusal,
// sentAsEbayMessage }.
export function sendReplyAsEbayMessage(suggestionId, { humanReply } = {}) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/send-as-ebay-message`, { humanReply })
}

export function retrySuggestionSend(suggestionId, { humanReply, emailTo, emailSubject, emailAttachments, replyMode, startNewContact, ccAddresses, confirmPromise, acknowledgedWarnings, skippedSteps } = {}) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/retry-send`, { humanReply, emailTo, emailSubject, emailAttachments, replyMode, startNewContact, ccAddresses, confirmPromise, acknowledgedWarnings, skippedSteps })
}

// Compose and send a brand-new, ad-hoc reply on a conversation that's already
// closed (no pending suggestion left to Approve). to/subject/replyMode are
// m365_email-only (see SuggestionPanel.jsx's onSendClosedM365Reply) — omit
// for eBay, where falsy/omitted values fall through to the server's own
// auto-derivation unchanged (see server/src/routes/ai.js's /manual-reply
// route). Returns { suggestion, conversationStatus, replySendError }, same
// shape as decideSuggestion's response.
// "New" email (2026-10-08): the From options (each service@ account with its
// live signature template), and the send, which creates the closed contact.
// A refusal throws with err.body = { error, message }.
export function fetchNewEmailOptions() {
  return getJson('/api/m365/new-email/options').then((d) => d.options ?? [])
}

export function sendNewEmail({ fromAccount, to, cc, subject, bodyHtml, attachments }) {
  return postJson('/api/m365/new-email', { fromAccount, to, cc, subject, bodyHtml, attachments })
}

// Where an internal notice's reply goes (2026-11-04): { target }.
export function getNoticeReplyTarget(conversationId) {
  return getJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/notice-reply-target`)
}

export function sendManualReply(conversationId, message, { to, subject, replyMode, startNewContact, insertedTemplate, ccAddresses, confirmRecentReply } = {}) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/manual-reply`, { message, to, subject, replyMode, startNewContact, insertedTemplate, ccAddresses, confirmRecentReply })
}

// Related contacts (2026-10-20). "Close Duplicates" on a primary: closes its
// open duplicates as "duplicate of #<this>", before the reply (close first:
// no confirm). Returns { closed: [{ id, convNumber }], reason }.
export function closeDuplicates(conversationId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/close-duplicates`, {})
}

// "Close As Duplicate" on a duplicate. Returns { closed, reason }.
export function closeAsDuplicate(conversationId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/close-as-duplicate`, {})
}

// "Close Related" on a primary: closes its open same-issue contacts as
// "covered by #<this>". Returns { closed, reason }.
export function closeRelated(conversationId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/close-related`, {})
}

// "Close As Related" on a same-issue contact. Returns { closed, reason }.
export function closeAsRelated(conversationId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/close-as-related`, {})
}

// The "related" chip's list: this contact's related contacts the viewer may
// open, { members: [{ id, convNumber, status, relation }] }.
export function fetchRelatedContacts(conversationId) {
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/related`)
}

// Mark a suggestion as spam: sets intent='spam', status='dismissed', closes
// conversation. Returns { suggestion, conversationStatus }.
export function markAsSpam(suggestionId) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/mark-spam`, {})
}

// Toggle "Reviewed by Mgmt" on a spam-marked suggestion — any signed-in agent
// (csr/admin) may call this. Returns { suggestion }.
export function setReviewedByMgmt(suggestionId, reviewed) {
  return patchJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/reviewed`, { reviewed })
}

// Execute a single proposed action step without recording a human decision.
// actionInputs: { [stepIndex]: value } — CSR-supplied values (e.g. reason code).
// source: 'manual_panel' when triggered from ManualActionsSidebar.jsx — tags
// every call this executes in the Activity Log, distinguishing it from the
// same route triggered via SuggestionPanel.jsx's own Process button.
// Returns { success, result, skipped } on success; { success: false, error, response } on failure.
// A task's moved / not-moved choice (2026-10-15): recorded on the suggestion
// (by whom, when). Returns { choice: { choice, by, at, stepIndex } }.
// date (2026-10-21): a Return Status stage's date, YYYY-MM-DD.
// why (2026-10-27, the resolution switch): out_of_stock or csr_choice.
export function chooseTaskPath(suggestionId, index, choice, date = null, why = null) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/task-path`, { index, choice, ...(date ? { date } : {}), ...(why ? { why } : {}) })
}

// The admin's path on a shadow draft's replacement or refund step
// (2026-10-27): { path, why }; display only.
export function saveShadowResolution(conversationId, shadowId, key, { path, why = null }) {
  return putJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/shadow-drafts/${encodeURIComponent(shadowId)}/resolutions/${encodeURIComponent(key)}`, { path, why })
}

// "Refund on the return / inquiry" (2026-10-29): a seller refund step whose
// Process stopped because an eBay return or INR inquiry is open on the order,
// rebuilt on that route. Reads only; nothing is refunded. Returns
// { route, ebayRefundRoute, actionIndex, proposedActions }.
export function rerouteEbayRefund(suggestionId, index) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/actions/${encodeURIComponent(index)}/ebay-refund-reroute`, {})
}

// The RMA number for an RMARTN Cust Pays reply's Reference ID (2026-10-27):
// the processed return step's RMA, read again for up to 5 s, else the
// order's number with the fallback note. { value, source, note }.
export function fetchRmaReference(suggestionId, index, { trqRmaId = null, erpRmaId = null } = {}) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/actions/${encodeURIComponent(index)}/rma-reference`, { trqRmaId: trqRmaId != null ? String(trqRmaId) : null, erpRmaId: erpRmaId ?? null })
}

// The link to the correct part (2026-10-27): its SKU and Item Info's
// warranty. { link, sku, warranty, needsSku }.
export function fetchPartLink(suggestionId, { link = '', sku = '' } = {}) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/part-link`, { link, ...(sku ? { sku } : {}) })
}

// "Process all steps and send reply" (2026-10-23): runs the listed steps in
// order and stops at the first failure. Returns { results, failed }; the
// reply is sent separately (decideSuggestion) only when nothing failed.
// The agent draft panel's step checkboxes (2026-10-26): the whole unchecked
// set, recorded on the suggestion. { unchecked, entry }.
export function saveStepChecks(suggestionId, unchecked) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/step-checks`, { unchecked })
}

// "Close anyway" on the untold-customer reminder (2026-10-30,
// manualStepReply.js): the Other Actions steps whose reply was never pasted
// or sent, recorded in the Activity Log with ids and a reason code only.
// via: 'dismiss' | 'close' | 'close_tab' | 'switch_contact'.
export function recordUntoldCustomerClose(suggestionId, { steps = [], via = 'dismiss' } = {}) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/untold-customer-close`, { steps, via })
}

// replyCarrierIndex (2026-10-27): the step that carries the reply as its
// eBay comment (the reply is then not sent again); the result then has
// replySentBy and conversationStatus.
export function processSteps(suggestionId, indexes, actionInputs = {}, { replyCarrierIndex = null } = {}) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/process-steps`, { indexes, actionInputs, ...(replyCarrierIndex != null && replyCarrierIndex >= 0 ? { replyCarrierIndex } : {}) })
}

export function executeStepAction(suggestionId, actionIndex, actionInputs = {}, { source } = {}) {
  return postJson(
    `/api/ai/suggestions/${encodeURIComponent(suggestionId)}/actions/${actionIndex}/execute`,
    { actionInputs, ...(source ? { source } : {}) }
  )
}

// Non-destructive disposition preview for a process_return_refund step —
// runs the same TRQ disposition check the execute route would, without
// executing any of the resulting write chain. source: see executeStepAction.
export function checkStepAction(suggestionId, actionIndex, actionInputs = {}, { source } = {}) {
  return postJson(
    `/api/ai/suggestions/${encodeURIComponent(suggestionId)}/actions/${actionIndex}/check`,
    { actionInputs, ...(source ? { source } : {}) }
  )
}

// The disposition check of a Process return/refund step (2026-10-27, Check
// and Check again): TRQ's checkDisposition for exactly these items and this
// reason code, and the refund breakdown. Reads only. step: { suggestionId,
// actionIndex } for the main panel's step, { shadowDraftId, stepKey } for
// the agent's. extra: shippingPaymentOption, packages, dispositionOverride,
// email, ccInternalCopy, ccAddress (the main panel's, so the calls it shows
// follow them).
export function runDispositionCheck(conversationId, { erpOrderId, items, reasonCode, ...rest }) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/disposition-check`, { erpOrderId, items, reasonCode, ...rest })
}

// Template picker: fills a chosen template's Process-time tokens
// (INSERTRMANUMBER/INSERTAMOUNT/INSERTTYPE/INSERTDAYS) from real facts the
// server gathers for this conversation, before SuggestionPanel.jsx's own
// applyTemplate() runs its usual substitution/merge logic. facts —
// erpRmaId/itemsTotal/shippingDeduction/shippingPaymentOption — are read by
// the CALLER from its own in-memory stepStates/actionInputs (no persisted,
// structured record of a step's execution result exists to read these back
// from server-side); paymentInstrumentType is resolved server-side instead,
// from the suggestion's own persisted action params. Returns
// { content, unresolved }. Never throws for a normal "couldn't resolve
// everything" outcome (unresolved is just informational) — a genuine
// network/HTTP failure still rejects, and callers must catch it and fall
// back to inserting the unfilled template rather than blocking the insert.
export function fillTemplateTokens(suggestionId, templateId, facts = {}) {
  return postJson(
    `/api/ai/suggestions/${encodeURIComponent(suggestionId)}/templates/${encodeURIComponent(templateId)}/fill`,
    facts
  )
}

// Other Actions → "Message the customer" (eBay orders only, 2026-10-30):
// the draft ({ step, erpOrderId, templateFillSuggestionId }), and the send
// ({ sent, buyerId, sentAt, ebayMessageId, ... } or { sent: false, error }).
export function fetchMessageCustomer(conversationId) {
  return getJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/message-customer`)
}
export function sendMessageCustomer(conversationId, text) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/message-customer/send`, { text })
}
// "Sent response through eBay Portal" after eBay's daily limit stopped the
// send (2026-11-02), and a template filled from the order on a contact with
// no AI suggestion ({ content }).
export function markMessageCustomerSentInSellerHub(conversationId, text) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/message-customer/mark-sent`, { text })
}
export function fillMessageCustomerTemplate(conversationId, templateId) {
  return postJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/message-customer/templates/${encodeURIComponent(templateId)}/fill`, {})
}

// Manual actions sidebar: request an action the AI didn't propose this turn.
// Reuses a matching proposedActions[] entry on the conversation's current
// suggestion if one already exists, otherwise grounds + builds it on demand.
// Returns { suggestionId, actionIndex, reused }, submittable via the existing
// executeStepAction/checkStepAction routes above like any other proposed step.
export function requestManualAction(conversationId, type) {
  return postJson(
    `/api/ai/conversations/${encodeURIComponent(conversationId)}/manual-actions`,
    { type }
  )
}

// Other Actions' "Resend Order/Shipping Confirmation Email" buttons — a plain
// TRQ write, not routed through the proposed-action/suggestion machinery.
// kind is 'order' | 'shipping'. Throws (via postJson) with err.body.error on
// failure — see ManualActionsSidebar.jsx's resendConfirmationErrorMessage.
export function resendConfirmationEmail(conversationId, kind, email) {
  return postJson(
    `/api/ai/conversations/${encodeURIComponent(conversationId)}/resend-confirmation`,
    { kind, email }
  )
}

// Translate non-English inbound messages and the latest suggestion's draft reply
// for a conversation. Caches results server-side; safe to call repeatedly.
// Returns { messages: { [messageId]: text }, draftReply: text | null, history: (text | null)[] }.
// history is index-aligned with the suggestion's modelMeta.inrHistory — computed
// fresh on every call rather than cached (inrHistory has no stable per-entry id).
// Just the latest suggestion's agent draft in English (2026-10-20), admin
// only: { shadowDraft: { suggestionId, reply, orderNote, summary, nextSteps } }
// or { shadowDraft: null }.
export function fetchShadowDraftTranslation(conversationId) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/shadow-draft-translation`, {})
}

export function fetchTranslations(conversationId) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/translations`, {})
}

// "Found the right part?" (2026-10-30): { status, sku, message, reply,
// english, languageName, steps, canUseAnyway }. Reads only.
export function findCorrectPart(suggestionId, { input, useAnyway = false } = {}) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/correct-part`, { input, useAnyway })
}

// The reply box in English as the CSR edits it (2026-10-30): { english,
// language }. english null when the text is English.
export function translateDraft(conversationId, text, { html = false, signal } = {}) {
  return postJson(`/api/conversations/${encodeURIComponent(conversationId)}/draft-translation`, { text, html }, { signal })
}

// The declined Website order the latest suggestion matched (2026-10-19), read
// live: { declinedOrder, warnings } or { declinedOrder: null }.
export function fetchDeclinedOrder(conversationId) {
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/declined-order`)
}

// fresh (2026-10-29): the order picker and Retry, which skip the server's
// one-minute order cache (X-Order-Fresh) and any prefetched response.
// Otherwise a request prefetched for this contact (orderSummaryPrefetch.js)
// is taken instead of a new one.
export function fetchOrderSummary(conversationId, { fresh = false } = {}) {
  if (!fresh) {
    const prefetched = takePrefetched(conversationId, 'summary')
    if (prefetched) return prefetched
  }
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/order-summary`, { headers: fresh ? { 'X-Order-Fresh': '1' } : null })
}

// The one genuinely slow, per-item piece of order-summary data (per-item
// Item Info/stock/fitment) — split into its own route so the rest of
// order-summary's fields don't wait on it. Returns { itemDetails }, null
// (m365_email/testing-period only) when the gate doesn't apply, TRQ is
// disabled, or no order was found. Fired alongside fetchOrderSummary(), not
// after it.
//
// part (2026-10-29): 'details' (Stock, Tags, Part Details) or 'fitment' (Fit),
// sent as X-Items-Part, so the fitment model call never holds up the rest.
// Returns { erpOrderId, slowFields, orderLines } then; with no part, also the
// full rows (itemDetails). fresh: as fetchOrderSummary().
export function fetchOrderSummaryItems(conversationId, { part = null, fresh = false } = {}) {
  if (!fresh && part) {
    const prefetched = takePrefetched(conversationId, part)
    if (prefetched) return prefetched
  }
  const headers = { ...(part ? { 'X-Items-Part': part } : {}), ...(fresh ? { 'X-Order-Fresh': '1' } : {}) }
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/order-summary/items`, { headers })
}

// Starts a contact's header reads now (orderSummaryPrefetch.js): on opening a
// contact, beside its own request, and on hover over a queue row.
export function prefetchContactOrderSummary(conversationId) {
  return prefetchOrderSummary(conversationId, {
    summary: (id) => getJson(`/api/conversations/${encodeURIComponent(id)}/order-summary`),
    details: (id) => getJson(`/api/conversations/${encodeURIComponent(id)}/order-summary/items`, { headers: { 'X-Items-Part': 'details' } }),
    fitment: (id) => getJson(`/api/conversations/${encodeURIComponent(id)}/order-summary/items`, { headers: { 'X-Items-Part': 'fitment' } }),
  })
}

// The eBay listing an eBay-channel conversation started from: { listingId,
// sku, title, source } (Lab-only experiment, 2026-10-07). The server asks Item
// Info; the browser never does.
export function fetchListingItem(conversationId) {
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/listing-item`)
}

// A Sales Hold's "Other Messages" list, split off the header's order-summary
// request so the header never waits on it (2026-10-07).
export function fetchOrderSummaryOtherConversations(conversationId) {
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/order-summary/other-conversations`)
}

// Raw TRQ notes for a single document — the conversation's main order, or any
// related/child document (return/replacement/RMA/etc.) — for the notepad-icon
// floating notes window. Unfiltered, unlike the AI-drafter grounding text.
export function fetchOrderNotes(conversationId, erpOrderId) {
  return getJson(`/api/conversations/${encodeURIComponent(conversationId)}/notes?erpOrderId=${encodeURIComponent(erpOrderId)}`)
}

// Admin triage view: pending suggestions with conversation context.
export function fetchTriageSuggestions({ limit, offset } = {}) {
  const qs = new URLSearchParams()
  if (limit != null) qs.set('limit', limit)
  if (offset != null) qs.set('offset', offset)
  const q = qs.toString()
  return getJson(`/api/ai/suggestions/triage${q ? `?${q}` : ''}`)
    .then((d) => ({ suggestions: d.suggestions ?? [], trqClientCode: d.trqClientCode ?? null }))
}

// --- Admin helpers ----------------------------------------------------------

async function patchJson(path, body) {
  const token = await getAccessToken()
  const res = await fetch(path, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (res.status === 204) return null
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  return res.json()
}

async function putJson(path, body) {
  const token = await getAccessToken()
  const res = await fetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (res.status === 204) return null
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  return res.json()
}

async function deleteReq(path) {
  const token = await getAccessToken()
  const res = await fetch(path, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!res.ok && res.status !== 204) throw new Error(`${path} -> ${res.status}`)
}

// Services
export const adminServices = {
  list: () => getJson('/api/admin/services').then((d) => d.services ?? []),
  get: (id) => getJson(`/api/admin/services/${id}`).then((d) => d.service),
  create: (data) => postJson('/api/admin/services', data).then((d) => d.service),
  update: (id, data) => putJson(`/api/admin/services/${id}`, data).then((d) => d?.service),
  delete: (id) => deleteReq(`/api/admin/services/${id}`),
  listDispositions: (id) => getJson(`/api/admin/services/${id}/dispositions`).then((d) => d.dispositions ?? []),
  createDisposition: (id, data) => postJson(`/api/admin/services/${id}/dispositions`, data).then((d) => d.disposition),
  updateDisposition: (id, did, data) => putJson(`/api/admin/services/${id}/dispositions/${did}`, data).then((d) => d?.disposition),
  deleteDisposition: (id, did) => deleteReq(`/api/admin/services/${id}/dispositions/${did}`),
  importDispositions: (id, dispositions) => postJson(`/api/admin/services/${id}/dispositions/import`, { dispositions }).then((d) => d.dispositions ?? []),
  exportDispositionsUrl: (id) => `/api/admin/services/${id}/dispositions/export`,
}

// Teams
export const adminTeams = {
  list: () => getJson('/api/admin/teams').then((d) => d.teams ?? []),
  get: (id) => getJson(`/api/admin/teams/${id}`).then((d) => d.team),
  create: (data) => postJson('/api/admin/teams', data).then((d) => d.team),
  update: (id, data) => putJson(`/api/admin/teams/${id}`, data).then((d) => d?.team),
  delete: (id) => deleteReq(`/api/admin/teams/${id}`),
}

// Agents
export const adminAgents = {
  list: () => getJson('/api/admin/agents').then((d) => d.agents ?? []),
  get: (id) => getJson(`/api/admin/agents/${id}`).then((d) => d.agent),
  create: (data) => postJson('/api/admin/agents', data).then((d) => d.agent),
  update: (id, data) => putJson(`/api/admin/agents/${id}`, data).then((d) => d?.agent),
  delete: (id) => deleteReq(`/api/admin/agents/${id}`),
}

// Agent Access
export const adminAgentAccess = {
  list: () => getJson('/api/admin/agent-access').then((d) => d.agentAccess ?? []),
  setServices: (agentId, serviceIds) => putJson(`/api/admin/agent-access/${agentId}`, { serviceIds }),
  addService: (agentId, serviceId) => postJson(`/api/admin/agent-access/${agentId}/services/${serviceId}`, {}),
  removeService: (agentId, serviceId) => deleteReq(`/api/admin/agent-access/${agentId}/services/${serviceId}`),
}

// Routing
export const adminRouting = {
  listRules: () => getJson('/api/admin/routing/rules').then((d) => d.rules ?? []),
  createRule: (data) => postJson('/api/admin/routing/rules', data).then((d) => d.rule),
  updateRule: (id, data) => putJson(`/api/admin/routing/rules/${id}`, data).then((d) => d?.rule),
  deleteRule: (id) => deleteReq(`/api/admin/routing/rules/${id}`),
  getConfig: () => getJson('/api/admin/routing/config').then((d) => d.config ?? []),
  updateConfig: (mediaType, data) => putJson(`/api/admin/routing/config/${mediaType}`, data).then((d) => d?.config),
}

// Hours of Operation
export const adminHours = {
  list: () => getJson('/api/admin/hours').then((d) => d.hours ?? []),
  get: (id) => getJson(`/api/admin/hours/${id}`).then((d) => d.hoo),
  create: (data) => postJson('/api/admin/hours', data).then((d) => d.hoo),
  update: (id, data) => putJson(`/api/admin/hours/${id}`, data).then((d) => d?.hoo),
  delete: (id) => deleteReq(`/api/admin/hours/${id}`),
  setSchedule: (id, schedule) => putJson(`/api/admin/hours/${id}/schedule`, { schedule }),
  addHoliday: (id, data) => postJson(`/api/admin/hours/${id}/holidays`, data).then((d) => d.holiday),
  updateHoliday: (id, hid, data) => putJson(`/api/admin/hours/${id}/holidays/${hid}`, data).then((d) => d?.holiday),
  deleteHoliday: (id, hid) => deleteReq(`/api/admin/hours/${id}/holidays/${hid}`),
}

// Spam rules (admin-only CRUD)
export const adminSpam = {
  // Every registered account (company x client) -- feeds the rules list's
  // account-shortcut row. A new account (e.g. AM_WEBSITE) appears here
  // automatically once registered server-side, no UI change needed.
  accounts: () => getJson('/api/admin/spam/accounts').then((d) => d.accounts ?? []),
  list: () => getJson('/api/admin/spam/rules').then((d) => d.rules ?? []),
  // Always an array — one row for a single company+channel selection, or one
  // per combination when the create dialog's multi-select fans out into more.
  create: (data) => postJson('/api/admin/spam/rules', data).then((d) => d.rules ?? []),
  // A single rule object for a plain single-value edit; an ARRAY when the
  // edit's multi-select added scope and the server fanned out into more
  // than one row (see admin-spam.js's PUT route and
  // updateSpamRuleWithFanOut()).
  update: (id, data) => putJson(`/api/admin/spam/rules/${id}`, data).then((d) => d.rules ?? d.rule),
  delete: (id) => deleteReq(`/api/admin/spam/rules/${id}`),
  // ruleIds: every existing rule id, in the desired new matching-precedence
  // order — real behavioral significance (spam.js's checkSpam() returns the
  // first match), not just display order.
  reorder: (ruleIds) => patchJson('/api/admin/spam/rules/reorder', { ruleIds }).then((d) => d.rules ?? []),
}

// Feedback: submit from agent panel (csr + admin), review/manage from admin page.
// screenshot, when present, is { data: base64, contentType }, built by
// SuggestionPanel.jsx from feedbackScreenshot.js's silent capture.
// screenshotStatus (2026-10-20): why a screenshot is or isn't attached,
// 'ok' | 'timeout' | 'error' | 'too_large' | 'none' (feedbackScreenshot.js).
// uiState (2026-10-26, feedbackUiState.js): the browser's half of the
// snapshot; the server builds the rest.
// screenState (2026-10-29, feedbackScreenState.js): the full screen state,
// stored apart from the snapshot, admin-only.
export function submitFeedback(conversationId, { suggestionId, intent, message, screenshot, screenshotStatus, uiState, screenState }) {
  return postJson('/api/admin/feedback', { conversationId, suggestionId, intent, message, screenshot, screenshotStatus, uiState, screenState }).then((d) => d.feedback)
}

// "As the CSR saw it" (2026-10-29): a report's screen state with the
// suggestion, its versions, what each processed step submitted and the
// report's facts. Admin-only.
export function fetchFeedbackScreenState(feedbackId) {
  return getJson(`/api/admin/feedback/${encodeURIComponent(feedbackId)}/screen-state`)
}

// The Send Feedback reports on one contact, for the Activity Log (admin-only).
export function fetchFeedbackForConversation(conversationId) {
  return getJson(`/api/admin/feedback/by-conversation/${encodeURIComponent(conversationId)}`).then((d) => d.feedback ?? [])
}

// Every recorded step on a contact with what it submitted (2026-10-29), for
// the Activity Log. Contact-gated.
export function fetchStepExecutions(conversationId) {
  return getJson(`/api/ai/conversations/${encodeURIComponent(conversationId)}/step-executions`).then((d) => d.executions ?? [])
}

// Feedback groups, the daily digest and the triage Retry (2026-10-26).
export function fetchFeedbackGroups() {
  return getJson('/api/admin/feedback/groups').then((d) => d.groups ?? [])
}

export function fetchFeedbackDigest(date) {
  return getJson(`/api/admin/feedback/digest${date ? `?date=${encodeURIComponent(date)}` : ''}`).then((d) => d.digest ?? null)
}

export function retryFeedbackTriage(id) {
  return postJson(`/api/admin/feedback/${encodeURIComponent(id)}/triage`, {}).then((d) => d.triage ?? null)
}

export function fetchFeedback({ includeCompleted = false } = {}) {
  return getJson(`/api/admin/feedback?includeCompleted=${includeCompleted}`).then((d) => d.feedback ?? [])
}

export function updateFeedbackItem(id, data) {
  return patchJson(`/api/admin/feedback/${encodeURIComponent(id)}`, data).then((d) => d?.feedback)
}

export function fetchFeedbackUsers() {
  return getJson('/api/admin/feedback/users').then((d) => d.users ?? [])
}

// One feedback item's screenshot as an object URL — same authenticated-fetch
// pattern as fetchAttachmentObjectUrl() above (an <img src> can't carry the
// Bearer token, so this can't be a plain URL). The caller revokes the URL
// when it's done with it. Admin-only route; a 404 (no screenshot, or past
// the 30-day retention window) surfaces as a thrown Error.
export async function fetchFeedbackScreenshotObjectUrl(feedbackId) {
  const token = await getAccessToken()
  const path = `/api/admin/feedback/${encodeURIComponent(feedbackId)}/screenshot`
  const res = await fetch(path, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!res.ok) throw new Error(`${path} -> ${res.status}`)
  return URL.createObjectURL(await res.blob())
}

// Flows automation catalog (admin-only). flowKey may contain a colon (e.g.
// "replacement:damaged") — always encodeURIComponent it.
export function fetchFlows() {
  return getJson('/api/admin/flows').then((d) => d.flows ?? [])
}

// team (2026-10-07): flows are keyed on (flowKey, company, channel, team,
// client); omitted, the server reads Customer Service.
export function updateFlowAutomate(flowKey, { company, channel, client, team, automate, note }) {
  return patchJson(`/api/admin/flows/${encodeURIComponent(flowKey)}`, { company, channel, client, team, automate, note }).then((d) => d?.flow)
}

export function updateFlowDescription(flowKey, company, channel, client, description, team) {
  return patchJson(`/api/admin/flows/${encodeURIComponent(flowKey)}/description`, { company, channel, client, team, description }).then((d) => d?.flow)
}

// Exact mirror of updateFlowDescription() above, for the Mermaid flowchart
// source instead.
export function updateFlowFlowchart(flowKey, company, channel, client, flowchartMermaid, team) {
  return patchJson(`/api/admin/flows/${encodeURIComponent(flowKey)}/flowchart`, { company, channel, client, team, flowchartMermaid }).then((d) => d?.flow)
}

// The one flow row for one account, cheaply (no audit log attached) — used
// by the flowchart viewer (opened in a new tab, so it can't reuse the Flows
// page's own already-loaded list).
export function fetchFlow(flowKey, company, channel, client, team) {
  return getJson(`/api/admin/flows/${encodeURIComponent(flowKey)}?company=${encodeURIComponent(company)}&channel=${encodeURIComponent(channel)}&client=${encodeURIComponent(client)}${team ? `&team=${encodeURIComponent(team)}` : ''}`).then((d) => d.flow)
}

// A real, honest deletion — same "cannot be undone" posture adminTemplates's
// own delete() has for templates. Query params, not a body, matching the
// route's own DELETE convention.
export function deleteFlow(flowKey, company, channel, client, team) {
  return deleteReq(`/api/admin/flows/${encodeURIComponent(flowKey)}?company=${encodeURIComponent(company)}&channel=${encodeURIComponent(channel)}&client=${encodeURIComponent(client)}${team ? `&team=${encodeURIComponent(team)}` : ''}`)
}

// The Queue's priority levels (migration 0093). fetchIntentPriorities() is the
// csr+admin map the Queue's own re-sort uses ({ tiers, defaultTier }); the
// other three back the admin-only Settings → Prioritization tab.
export function fetchIntentPriorities() {
  return getJson('/api/intent-priorities')
}

export function fetchAdminIntentPriorities() {
  return getJson('/api/admin/intent-priorities')
}

export function updateIntentPriority(intent, tier) {
  return putJson(`/api/admin/intent-priorities/${encodeURIComponent(intent)}`, { tier })
}

export function fetchIntentPriorityHistory() {
  return getJson('/api/admin/intent-priorities/history').then((d) => d.history ?? [])
}

// The Queue's quick-reply priority rules (migration 0167, admin-only,
// Settings → Prioritization): a customer who answers our message within N
// minutes moves up to level L while it is unanswered.
export function fetchQuickReplyRules() {
  return getJson('/api/admin/quick-reply-rules')
}

export function createQuickReplyRule(rule) {
  return postJson('/api/admin/quick-reply-rules', rule)
}

export function updateQuickReplyRule(id, changes) {
  return putJson(`/api/admin/quick-reply-rules/${encodeURIComponent(id)}`, changes)
}

export function fetchQuickReplyRuleHistory() {
  return getJson('/api/admin/quick-reply-rules/history').then((d) => d.history ?? [])
}

// Fired after an admin changes a level, so this tab's Queue re-sorts at once
// instead of on its next refresh.
export const INTENT_PRIORITIES_CHANGED_EVENT = 'cxhub:intent-priorities-changed'

// Per-company runtime switches (dark launches — migration 0029). `account` is
// an ACCOUNT code ('AM_EBAY'), matching what the route validates against.
// outboundMode ('off'|'test'|'live', migration 0062) is a fourth, m365_email-only
// switch — the per-account send mode, alongside ingestion/generation/surfaced.
export function fetchCompanySettings() {
  return getJson('/api/admin/companies').then((d) => d.companies ?? [])
}

export function updateCompanySettings(account, { ingestionEnabled, surfaced, generationEnabled, outboundMode }) {
  const body = {}
  // Send only the flag being changed — the server COALESCEs absent fields to
  // their current value, so omitting one is how you leave it untouched.
  if (ingestionEnabled !== undefined) body.ingestionEnabled = ingestionEnabled
  if (surfaced !== undefined) body.surfaced = surfaced
  if (generationEnabled !== undefined) body.generationEnabled = generationEnabled
  if (outboundMode !== undefined) body.outboundMode = outboundMode
  return patchJson(`/api/admin/companies/${encodeURIComponent(account)}`, body).then((d) => d?.settings)
}

export function fetchFlowLog(flowKey = null, company = null, channel = null, client = null, team = null) {
  const path = flowKey
    ? `/api/admin/flows/${encodeURIComponent(flowKey)}/log?company=${encodeURIComponent(company)}&channel=${encodeURIComponent(channel)}&client=${encodeURIComponent(client)}${team ? `&team=${encodeURIComponent(team)}` : ''}`
    : '/api/admin/flows/log'
  return getJson(path).then((d) => d.log ?? [])
}

// Roles page — per-agent conversation-visibility toggles (agent_permissions),
// the email-account permission-list overlay (email_account_lists), and their
// shared audit log. agentId is the agent_permissions row id — the same
// whether the row came from a login or from the Entra group sync ahead of
// one (the Entra object id is exposed separately as `oid`).
// Returns { agents, sync } — sync is the Entra agent sync's status
// (enabled/lastSuccessAt/lastError/...), for the page's "Refresh from Entra"
// line.
export function fetchAgentPermissions() {
  return getJson('/api/admin/roles').then((d) => ({ agents: d.agents ?? [], sync: d.sync ?? null }))
}

// Runs the Entra group sync now. Resolves to the sync status; a Graph
// failure is reported in status.lastError (the table is left untouched),
// and a 409 means the flag is off (nothing to run).
export function runEntraAgentSync() {
  return postJson('/api/admin/roles/sync', {}).then((d) => d?.sync ?? null)
}

export function updateAgentPermission(agentId, field, value, note) {
  return patchJson(`/api/admin/roles/${encodeURIComponent(agentId)}`, { field, value, note: note ?? null }).then((d) => d?.agent)
}

export function bulkUpdateAgentPermissions(agentIds, field, value, note) {
  return postJson('/api/admin/roles/bulk', { agentIds, field, value, note: note ?? null }).then((d) => d ?? { agents: [], count: 0 })
}

export function fetchEmailAccountLists() {
  return getJson('/api/admin/roles/email-accounts').then((d) => d.accounts ?? [])
}

export function updateEmailAccountList(address, list) {
  return patchJson(`/api/admin/roles/email-accounts/${encodeURIComponent(address)}`, { list }).then((d) => d?.account)
}

export function fetchAgentPermissionsLog({ changedBy, targetAgentId, from, to } = {}) {
  const params = new URLSearchParams()
  if (changedBy) params.set('changedBy', changedBy)
  if (targetAgentId) params.set('targetAgentId', targetAgentId)
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  const qs = params.toString()
  return getJson(`/api/admin/roles/log${qs ? `?${qs}` : ''}`).then((d) => d.log ?? [])
}

// Templates — agent read (any role) and admin CRUD.
export const adminTemplates = {
  list: () => getJson('/api/admin/templates').then((d) => d.grouped ?? {}),
  create: (data) => postJson('/api/admin/templates', data).then((d) => d.template),
  update: (id, data) => patchJson(`/api/admin/templates/${encodeURIComponent(id)}`, data).then((d) => d?.template),
  delete: (id) => deleteReq(`/api/admin/templates/${id}`),
  // The house style profile of a template root (2026-11-02), read-only.
  houseStyle: (root) => getJson(`/api/admin/templates/house-style?root=${encodeURIComponent(root)}`).then((d) => d?.profile ?? null),
}

// Flows automation failures (admin-only) — the Failures page + top-nav notification.
export function fetchAutomationFailures() {
  return getJson('/api/admin/automation-failures').then((d) => d.failures ?? [])
}

export function resolveAutomationFailure(id) {
  return postJson(`/api/admin/automation-failures/${encodeURIComponent(id)}/resolve`, {}).then((d) => d?.failure)
}

export function fetchReportingSummary({ preset, from, to, companies, channel } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  if (companies?.length) qs.set('companies', companies.join(','))
  if (channel && channel !== 'all') qs.set('channel', channel)
  return getJson(`/api/admin/reporting/summary?${qs.toString()}`)
}

// Live count of conversations currently in the queue (not scoped to a
// date range) -- the current backlog, independent of Reporting's presets.
export function fetchReportingQueueCount(companies, channel) {
  const qs = new URLSearchParams()
  if (companies?.length) qs.set('companies', companies.join(','))
  if (channel && channel !== 'all') qs.set('channel', channel)
  return getJson(`/api/admin/reporting/queue?${qs.toString()}`).then((d) => d.queueCount)
}

// Per-agent contacts-per-hour throughput. Returns { agents, coverageStartsAt,
// totalEvents, uncoveredCount } -- coverageStartsAt is the earliest data
// this metric can possibly cover (conversation_assignments only exists from
// migration 0033 forward), for the caller to clamp/disable earlier dates.
export function fetchContactsPerHour({ preset, from, to, companies, channel } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  if (companies?.length) qs.set('companies', companies.join(','))
  if (channel && channel !== 'all') qs.set('channel', channel)
  return getJson(`/api/admin/reporting/contacts-per-hour?${qs.toString()}`)
}

// Per-agent online (connected to CXHub) vs. active (claimed-conversation)
// time, so idle time -- online but nothing claimed -- is visible directly.
// { agents: [{key, points: {day: {onlineHours, activeHours, idleHours}}}],
// dates }. onlineHours has no per-account scope of its own (being logged
// into CXHub isn't tied to an eBay account) -- `companies` here only
// narrows which activeHours count toward the comparison.
export function fetchOnlineTime({ preset, from, to, companies, channel } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  if (companies?.length) qs.set('companies', companies.join(','))
  if (channel && channel !== 'all') qs.set('channel', channel)
  return getJson(`/api/admin/reporting/online-time?${qs.toString()}`)
}

// Per-intent handle-time throughput -- a sibling to fetchContactsPerHour,
// same response shape but keyed by `intents` instead of `agents`.
export function fetchContactsPerHourByIntent({ preset, from, to, companies, channel } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  if (companies?.length) qs.set('companies', companies.join(','))
  if (channel && channel !== 'all') qs.set('channel', channel)
  return getJson(`/api/admin/reporting/contacts-per-hour-by-intent?${qs.toString()}`)
}

// How long a contact stays open with an agent, claim to action. Same
// coverage-window caveat as fetchContactsPerHour -- it reads the assignment
// audit trail, which only exists from migration 0033 forward.
export function fetchHandleTime({ preset, from, to, companies, channel } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  if (companies?.length) qs.set('companies', companies.join(','))
  if (channel && channel !== 'all') qs.set('channel', channel)
  return getJson(`/api/admin/reporting/handle-time?${qs.toString()}`)
}

// ASA (average speed of answer) per account, in weeks-ending-Saturday
// buckets -- deliberately no `companies` param, this always covers every
// registered account (see the route's own comment for why).
export function fetchAsaByAccount({ weeks } = {}) {
  const qs = new URLSearchParams()
  if (weeks) qs.set('weeks', weeks)
  return getJson(`/api/admin/reporting/asa-by-account?${qs.toString()}`)
}

// ONE blended, company-wide weekly AHT for the QA KPI Dashboard (see
// getBlendedEmailAhtReport's own comment for the exact account pool and the
// Support Email exclusion) -- same weeks/shape convention as fetchAsaByAccount
// above, so the two stay comparable over the same window.
export function fetchQaKpiBlendedAht({ weeks } = {}) {
  const qs = new URLSearchParams()
  if (weeks) qs.set('weeks', weeks)
  return getJson(`/api/admin/reporting/qa-kpi-blended-aht?${qs.toString()}`)
}

// Per-account ASA for the QA KPI Dashboard's own preview (2026-10-15) --
// deliberately NOT fetchAsaByAccount above: that one pools each brand's
// Website into its Email row, so it has no standalone 1A_WEBSITE/AM_WEBSITE
// account left to show. This hits the plain, unpooled report instead, so
// Patrick can report Website's own SLA separately. Same weeks/shape
// convention as fetchAsaByAccount.
export function fetchQaKpiAsaByAccount({ weeks } = {}) {
  const qs = new URLSearchParams()
  if (weeks) qs.set('weeks', weeks)
  return getJson(`/api/admin/reporting/qa-kpi-asa-by-account?${qs.toString()}`)
}

// period: 'past' (last 8 weeks) or 'next' (next 4 weeks of schedule).
export function fetchForecastHeatmap(period = 'past') {
  return getJson(`/api/admin/reporting/forecasting/heatmap?period=${encodeURIComponent(period)}`)
}

// The Phone and Chat view (2026-10-24): volume, staffing plan and actuals on
// Patrick's 23% method, oldest week first, with the Erlang adequacy check.
export function fetchForecastPhoneChat() {
  return getJson('/api/admin/reporting/forecasting/phone-chat')
}

// The Email view (2026-10-27): CXHub email plus Bright Pattern Amazon as one weekly
// line, the staffing plan and each account's speed of answer against its target.
export function fetchForecastEmail() {
  return getJson('/api/admin/reporting/forecasting/email')
}

// Account Reports -> Contacts per Order (2026-10-27): order side by
// company + channel; `granularity` week (default), day or month.
export function fetchCpoReport({ granularity, periods } = {}) {
  const qs = new URLSearchParams()
  if (granularity) qs.set('granularity', granularity)
  if (periods) qs.set('periods', String(periods))
  return getJson(`/api/admin/reporting/cpo?${qs.toString()}`)
}

// The Support view (2026-10-28): CXHub Support email plus the Bright Pattern CSS and TRQ
// queues as one weekly line, the staffing plan and Support Email's speed of answer.
export function fetchForecastSupport() {
  return getJson('/api/admin/reporting/forecasting/support')
}

// Forecasting tab -> Overview (2026-10-29): every function's work against every team's scheduled
// hours, with the Seniors' measured CXHub help, and the work matrix.
export function fetchForecastOverview() {
  return getJson('/api/admin/reporting/forecasting/overview')
}

// AHT (average handle time) per account, same shape/conventions as
// fetchAsaByAccount above.
export function fetchAhtByAccount({ weeks } = {}) {
  const qs = new URLSearchParams()
  if (weeks) qs.set('weeks', weeks)
  return getJson(`/api/admin/reporting/aht-by-account?${qs.toString()}`)
}

// AHT by account, broken out by agent. granularity: 'day'|'week'|'month',
// periods: how many of that granularity's buckets back to show (the route
// applies its own per-granularity default when omitted).
export function fetchAhtByAccountPerRep({ granularity, periods } = {}) {
  const qs = new URLSearchParams()
  if (granularity) qs.set('granularity', granularity)
  if (periods) qs.set('periods', periods)
  return getJson(`/api/admin/reporting/aht-by-account-per-rep?${qs.toString()}`)
}

// The Scorecards tab's own report -- every agent's email-channel contacts,
// Email AHT, Support Email AHT, and schedule adherence, per bucket.
// granularity: 'week'|'month', periods: how many of that granularity's
// buckets back to fetch (the route applies its own per-granularity default
// when omitted; month always reaches the current, in-progress month).
export function fetchScorecards({ granularity, periods } = {}) {
  const qs = new URLSearchParams()
  if (granularity) qs.set('granularity', granularity)
  if (periods) qs.set('periods', periods)
  return getJson(`/api/admin/reporting/scorecards?${qs.toString()}`)
}

// Bright Pattern email results entered from Power BI (2026-11-02): { rows }.
export function fetchBpEmailStats() {
  return getJson('/api/admin/reporting/bp-email-stats')
}

// Save pasted lines (period, agent email, emails, AHT). A 400 carries
// err.body.errors, one message per bad line; nothing is saved then.
export function saveBpEmailStats(text) {
  return postJson('/api/admin/reporting/bp-email-stats', { text })
}

export function deleteBpEmailStat({ granularity, period, agentEmail }) {
  return postJson('/api/admin/reporting/bp-email-stats/delete', { granularity, period, agentEmail })
}

// The Scorecards weekly analysis's findings (fixed rules, no AI yet) for one
// completed week. `teams` is an array of team names, or omitted for every
// analyzed team; `weekEnding` defaults server-side to the last completed week.
export function fetchScorecardsInsights({ weekEnding, teams } = {}) {
  const qs = new URLSearchParams()
  if (weekEnding) qs.set('weekEnding', weekEnding)
  if (teams?.length) qs.set('teams', teams.join(','))
  return getJson(`/api/admin/reporting/scorecards/insights?${qs.toString()}`)
}

// The weekly write-up for one completed week (Scorecards weekly analysis,
// PR 2): { enabled, weekEnding, teams: [{ team, status, narrative, heldReason,
// generatedAt, ... }] }. `enabled: false` means the flag is off.
export function fetchScorecardsWriteup({ weekEnding } = {}) {
  const qs = new URLSearchParams()
  if (weekEnding) qs.set('weekEnding', weekEnding)
  return getJson(`/api/admin/reporting/scorecards/insights/writeup?${qs.toString()}`)
}

// Write a week now instead of waiting for Monday: every team without a write-up,
// or one team again with force. Same response shape as fetchScorecardsWriteup,
// plus `run` counts.
export function generateScorecardsWriteup({ weekEnding, team, force } = {}) {
  return postJson('/api/admin/reporting/scorecards/insights/writeup', { weekEnding, team, force })
}

// One agent's longest individual contacts in a range -- the per-contact
// drill-down behind Handle Time's "Longest" stat. `agent` is required.
export function fetchLongestAhtContacts({ preset, from, to, companies, channel, agent }) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  if (companies?.length) qs.set('companies', companies.join(','))
  if (channel && channel !== 'all') qs.set('channel', channel)
  qs.set('agent', agent)
  return getJson(`/api/admin/reporting/longest-aht-contacts?${qs.toString()}`)
}

// One contact's handle-time workings: its claims, pauses, parks and the sum
// the reports make for each decision or close (admin only).
export function fetchContactHandleTime(conv) {
  return getJson(`/api/admin/reporting/contact-handle-time?conv=${encodeURIComponent(conv)}`)
}

// On-demand AI summary of the contacts above -- same scope, re-derived
// server-side. Pass regenerate: true to bypass the server's cache.
export function fetchLongestAhtSummary({ agent, from, to, companies, channel, regenerate }) {
  return postJson('/api/admin/reporting/longest-aht-contacts/summary', { agent, from, to, companies: companies?.join(',') || undefined, channel: channel && channel !== 'all' ? channel : undefined, regenerate })
}

// Automation Reports tab: automation rate by flow + automation failure
// count by flow, both weekly, same shape/conventions as fetchAhtByAccount.
export function fetchAutomationTrends({ weeks } = {}) {
  const qs = new URLSearchParams()
  if (weeks) qs.set('weeks', weeks)
  return getJson(`/api/admin/reporting/automation-trends?${qs.toString()}`)
}

// On-demand AI week-over-week review of the two tables above -- same scope
// (weeks), re-derived server-side, plus the caller's current account-group
// filter. Pass regenerate: true to bypass the server's cache.
export function fetchAutomationTrendsSummary({ weeks, groups, regenerate }) {
  return postJson('/api/admin/reporting/automation-trends/summary', { weeks, groups, regenerate })
}

// Live supervisor dashboard (Reporting -> Dashboard tab) -- two
// no-argument "right now" reads, polled client-side. Separate calls (not
// one combined endpoint) so a failure/slow response in one never blanks
// the other.
export function fetchLiveAgents() {
  return getJson('/api/admin/reporting/live-agents')
}
export function fetchLiveQueues() {
  return getJson('/api/admin/reporting/live-queues')
}

// trqClientCode: conversation.trqClientCode (already exposed on every
// conversation — see repositories/conversations.js's own mapping) — lets a
// 1AW M365 conversation's CSR see the "1A Website" folder tree instead of
// the account's own (routes/templates.js's effectiveFolderConfig). Optional
// everywhere; omitting it is unchanged behavior for every other account.
// Returns { folders, groups }: groups (2026-10-22) names the trees the
// picker shows as their own labelled group ([{ label, prefix }]).
export function fetchTemplateFolders({ account, trqClientCode, team } = {}) {
  const qs = new URLSearchParams()
  if (account) qs.set('account', account)
  if (trqClientCode) qs.set('trqClientCode', trqClientCode)
  if (team) qs.set('team', team)
  const s = qs.toString()
  return getJson(`/api/templates/folders${s ? `?${s}` : ''}`).then((d) => ({ folders: d.folders ?? [], groups: d.groups ?? [] }))
}

// scope 'other-client' (2026-10-08): a folder from the picker's other-clients
// section; the server allows it only when it is in this conversation's own
// other-client set.
export function fetchTemplatesByFolder(folder, { account, trqClientCode, team, scope } = {}) {
  const qs = new URLSearchParams({ folder })
  if (account) qs.set('account', account)
  if (trqClientCode) qs.set('trqClientCode', trqClientCode)
  if (team) qs.set('team', team)
  if (scope) qs.set('scope', scope)
  return getJson(`/api/templates?${qs.toString()}`).then((d) => d.templates ?? [])
}

// The same company's other Customer Service client libraries for this
// conversation, as [{ label, prefix, folders }] (2026-10-08). [] on failure:
// the section is extra, never something the dialog depends on.
export function fetchOtherClientTemplateFolders({ account, trqClientCode, team } = {}) {
  const qs = new URLSearchParams()
  if (account) qs.set('account', account)
  if (trqClientCode) qs.set('trqClientCode', trqClientCode)
  if (team) qs.set('team', team)
  return getJson(`/api/templates/other-client-folders?${qs.toString()}`).then((d) => d.groups ?? []).catch(() => [])
}

export function searchTemplates(q, { account, trqClientCode, team } = {}) {
  const qs = new URLSearchParams({ q })
  if (account) qs.set('account', account)
  if (trqClientCode) qs.set('trqClientCode', trqClientCode)
  if (team) qs.set('team', team)
  return getJson(`/api/templates/search?${qs.toString()}`).then((d) => d.templates ?? [])
}

export function fetchTemplateSuggestions({ intent, account, trqClientCode, team } = {}) {
  const qs = new URLSearchParams()
  if (intent) qs.set('intent', intent)
  if (account) qs.set('account', account)
  if (trqClientCode) qs.set('trqClientCode', trqClientCode)
  if (team) qs.set('team', team)
  return getJson(`/api/templates/suggestions?${qs.toString()}`).then((d) => d.templates ?? [])
}

// Admin AI assistant (the chat sidebar). The server is stateless, so the whole
// visible transcript goes up on every turn; `messages` is [{ role, content }]
// with role 'user' | 'assistant', ending on a user turn.
//
// `context` optionally grounds the answer in the open conversation:
//   { conversationId, includeMessageText }. With includeMessageText false the
// server never reads message bodies at all; true opts into PII-redacted bodies.
// `systemAnalytics` lets the assistant query platform-wide operational data
// (volumes, intents, agents, QA, automation) to answer trending questions. It
// is independent of `context` — both can be on at once.
//
// Returns { reply, notices, queriesRun, usage, model, cost } — notices explain
// anything the server withheld or capped, queriesRun lists the queries the
// answer was built from, and cost is the server's estimate for THIS turn
// ({ usd, priced }; priced:false means unknown, not free).
//
// `signal` cancels the turn (the Stop button).
export async function askAssistant(messages, { context, systemAnalytics, model, signal } = {}) {
  return postJson('/api/admin/assistant/chat', {
    messages,
    ...(context ? { context } : {}),
    ...(systemAnalytics ? { systemAnalytics: true } : {}),
    ...(model ? { model } : {}),
  }, { signal })
}

// Deployments the assistant may run on, plus what we think they cost. Returns
// { models: [{ id, isDefault, pricePerMillion }], defaultModel, pricingConfigured }.
export async function fetchAssistantModels() {
  return getJson('/api/admin/assistant/models')
}

// The same context the assistant is given, rendered as a pasteable Markdown
// report — for an issue, or for handing to a coding agent that has the code but
// not the live data. `messages` is optional; including the session carries the
// diagnosis along with the evidence. Returns the report string.
export async function exportAssistantReport(context, messages) {
  const { report } = await postJson('/api/admin/assistant/export', {
    context,
    ...(messages?.length ? { messages } : {}),
  })
  return report
}

// Admin Costing view. params: { preset } or { from, to }, plus { groupBy }.
// On a non-2xx, throws an Error whose `.code` is the server's error string (e.g.
// 'cost_access_denied') so the view can show a tailored message.
export async function fetchCost({ preset, from, to, groupBy = 'service', env = null } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  qs.set('groupBy', groupBy)
  if (env) qs.set('env', env)
  const token = await getAccessToken()
  const res = await fetch(`/api/admin/cost?${qs.toString()}`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(body.error || `cost -> ${res.status}`)
    err.code = body.error || null
    throw err
  }
  return body
}

// Model-call ledger for the Costing view's second panel: every model call
// recorded on suggestions built in the window, by stage and by deployment,
// with a token total and a price estimate where the deployment is priced.
// Same window params as fetchCost(); same error convention.
export async function fetchCostLedger({ preset, from, to } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  const token = await getAccessToken()
  const res = await fetch(`/api/admin/cost/ledger?${qs.toString()}`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(body.error || `cost ledger -> ${res.status}`)
    err.code = body.error || null
    throw err
  }
  return body
}

// GET /api/admin/cost/generation-health -- failed draft generations by day,
// cause and account, for the same window as the ledger (CostView.jsx).
export async function fetchGenerationHealth({ preset, from, to } = {}) {
  const qs = new URLSearchParams()
  if (from && to) { qs.set('from', from); qs.set('to', to) }
  else if (preset) qs.set('preset', preset)
  const token = await getAccessToken()
  const res = await fetch(`/api/admin/cost/generation-health?${qs.toString()}`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(body.error || `generation health -> ${res.status}`)
    err.code = body.error || null
    throw err
  }
  return body
}

// Tasks (2026-10-12, Lab-only behind TASKS_ENABLED; server/src/routes/tasks.js).
// The list: { tasks, creators, orderLinks }.
export function fetchTasks(queryString = '') {
  return getJson(`/api/tasks${queryString}`)
}
// { task, events, orderLinks }.
export function fetchTask(id) {
  return getJson(`/api/tasks/${encodeURIComponent(id)}`)
}
// Create / edit return the server's validation errors as { errors } on a 400,
// so the form can show them next to the fields.
async function sendTask(method, path, body) {
  const t = await getAccessToken()
  const res = await fetch(path, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(`${path} -> ${res.status}`)
    err.status = res.status
    err.errors = json?.errors ?? null
    throw err
  }
  return json
}
export function createTask(body) {
  return sendTask('POST', '/api/tasks', body)
}
export function updateTask(id, body) {
  return sendTask('PATCH', `/api/tasks/${encodeURIComponent(id)}`, body)
}
// Close an open task with its required note (2026-10-13); its contact closes
// too. { task, events, orderLinks }.
export function closeTask(id, note) {
  return sendTask('POST', `/api/tasks/${encodeURIComponent(id)}/close`, { note })
}

// QA auditor (docs/qa-observer.md), admin only. The "who handled it" trend
// is CXHub's own data (QL-20); the rest is read from the auditor's own API
// through CXHub's read-only proxy, and CXHub stores none of it.
export function fetchHandlingTrend({ from, to } = {}) {
  const q = new URLSearchParams()
  if (from) q.set('from', from)
  if (to) q.set('to', to)
  return getJson(`/api/admin/qa-observer/handling-trend?${q}`)
}

export function fetchQaObserver(path, params = {}) {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  return getJson(`/api/admin/qa-observer/observer/${path}${q.toString() ? `?${q}` : ''}`)
}

// The one write through the auditor proxy (phase 3): a human ruling on a
// sampled day-7 audit, into the auditor's own database. Fixed values only;
// the server takes the ruler from the sign-in, never from this body.
export function postQaObserverRuling(body) {
  return postJson('/api/admin/qa-observer/observer/rulings', body)
}

// The quality loop's improvement work (phase 4, docs/quality-improvement.md):
// the AI share next to the auditor, the gaps, candidate fixes and their blind
// replays, the report to Jenn. A switch that is off answers 404; the error
// carries the status so the page can say so.
export async function fetchQuality(path, params = {}) {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  const token = await getAccessToken()
  const url = `/api/admin/quality/${path}${q.toString() ? `?${q}` : ''}`
  const res = await fetch(url, { headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
  if (!res.ok) {
    const err = new Error(`${url} -> ${res.status}`)
    err.status = res.status
    err.body = await res.json().catch(() => null)
    throw err
  }
  return res.json()
}

export function postQualityFix(body) {
  return postJson('/api/admin/quality/fixes', body)
}

export function withdrawQualityFix(id) {
  return postJson(`/api/admin/quality/fixes/${encodeURIComponent(id)}/withdraw`, {})
}

export function startQualityReplay(id, count) {
  return postJson(`/api/admin/quality/fixes/${encodeURIComponent(id)}/runs`, count ? { count } : {})
}

export function downloadQualityReport(format = 'markdown') {
  return downloadCsv(`/api/admin/quality/report?format=${format === 'json' ? 'json' : 'markdown'}`, format === 'json' ? 'cxhub-quality-report.json' : 'cxhub-quality-report.md')
}

// The agent agreement view on the Audit page (2026-10-23, admin only;
// server/src/routes/admin-shadow-agreement.js): per intent, the last 7 and 30
// days. No per-agent numbers.
export function fetchShadowAgreement() {
  return getJson('/api/admin/shadow-agreement')
}

// eBay's daily message limit (2026-10-29): "Sent response through eBay Portal" after the person
// sent the refused message in Seller Hub (our own record, no eBay call), and
// the Ops counts (admin).
export function markEbayStepSentInSellerHub(suggestionId, index, messageText) {
  return postJson(`/api/ebay-messages/suggestions/${encodeURIComponent(suggestionId)}/steps/${encodeURIComponent(index)}/mark-sent`, { messageText: messageText ?? '' })
}
export function markEbayReplySentInSellerHub(suggestionId, messageText) {
  return postJson(`/api/ebay-messages/suggestions/${encodeURIComponent(suggestionId)}/reply/mark-sent`, { messageText: messageText ?? '' })
}
export function fetchEbayMessageCounts(days = 14) {
  return getJson(`/api/ebay-messages/counts?days=${encodeURIComponent(days)}`)
}

// Return steps waiting on the OMS API (2026-10-29, server/src/routes/
// return-labels.js): a contact's checklists and the same order's waiting
// steps, "Check now", the admin list, the $0 RMA typical wait.
export function fetchConversationReturnLabels(conversationId) {
  return getJson(`/api/return-labels/conversations/${encodeURIComponent(conversationId)}`)
}
export function checkReturnLabelNow(id) {
  return postJson(`/api/return-labels/${encodeURIComponent(id)}/check`, {})
}
// "Record the label again" (2026-11-02): a failed step whose label was made
// but not recorded on the RMA. Records that label (never a new one) and
// sends it. { outcome: 'done' | 'failed', error, step }.
export function recordReturnLabelAgain(id) {
  return postJson(`/api/return-labels/${encodeURIComponent(id)}/record-again`, {})
}
export function fetchWaitingOnOms() {
  return getJson('/api/return-labels/admin')
}
export function fetchZeroDollarTiming() {
  return getJson('/api/return-labels/zero-dollar-timing')
}

// The replacement step's delivery estimate again (2026-11-02, Jenn;
// server/src/ai/replacement-edd.js): the person changed the step's items,
// quantities, ship-to ZIP or shipping method. Reads only (the storefront);
// logged on the suggestion with the step's index. selection: { items,
// shippingMethod, zipCode } (the ZIP only, never the rest of the address).
// Returns { estimate, previous }.
export function reestimateReplacementDelivery(suggestionId, actionIndex, selection) {
  return postJson(`/api/ai/suggestions/${encodeURIComponent(suggestionId)}/actions/${actionIndex}/delivery-estimate`, { selection })
}
